import {
    select as D3select,
    dispatch as D3dispatch,
    scaleTime as D3scaleTime,
    scaleLinear as D3scaleLinear,
    scaleOrdinal as D3scaleOrdinal,
    axisBottom as D3axisBottom,
    min as D3min,
    max as D3max,
    extent as D3extent,
    brushX as D3brushX,
    area as D3area,
    curveMonotoneX as D3curveMonotoneX,
    timeHour as D3timeHour,
} from 'd3';
import Moment from 'moment';

// a brush over an area chart
const D3TimelineBrush = function () {
    const dispatch = D3dispatch('changed');

    // overridable properties
    let color = D3scaleOrdinal();
    let sel = [Moment().subtract(1, 'h'), Moment()]; // incoming time selection
    let dataservice = {
        timestamp: d => d.timestamp,
        count: d => d.count,
        end: d => d.timestamp,
    }; // normalizers
    let height = 40;
    let width = 100;
    let start;
    let end;

    // global style units
    const handleHang = 3;
    const axisHeight = 10;
    const day = 86400000;
    const year = 365 * day;
    // make room for axis
    const margin = { top: 0, right: 10, bottom: 0, left: 0 };

    function my (selction) {
        if (!selction) return;
        selction.each(function (data, i) {
            // brush needs to be large enough for actual data, reported start/time and user time selections
            const xmin = D3min(data, function (d) { return dataservice.timestamp(d) }) || new Date();
            const xmax = D3max(data, function (d) { return dataservice.end(d) }) || new Date();
            const s = start ? new Date(start) : xmin;
            const e = end ? new Date(end) : xmax;
            const ss = sel && sel.length > 1 && sel[0] ? new Date(sel[0]) : xmin;
            const se = sel && sel.length > 1 && sel[1] ? new Date(sel[1]) : xmax;
            const x = D3scaleTime()
                .domain([
                    [xmin, s, ss].filter(t => t).reduce((a, c) => c.getTime() < a.getTime() ? c : a, xmin),
                    [xmax, e, se].filter(t => t).reduce((a, c) => c.getTime() > a.getTime() ? c : a, xmax),
                ])
                .range([handleHang, width - handleHang - margin.left - margin.right]);

            const y = D3scaleLinear()
                .domain(D3extent(data || [], dataservice.count))
                .range([height - handleHang - axisHeight, handleHang]);

            // render the brush
            const brush = D3brushX()
                .extent([
                    [handleHang, handleHang],
                    [width - handleHang - margin.left - margin.right, height - handleHang - axisHeight]
                ]);
            brush.on('end', brushMoved);

            const area = D3area().curve(D3curveMonotoneX)
                .x(d => x(dataservice.timestamp(d)))
                .y1(d => y(dataservice.count(d)))
                .y0(height - handleHang - axisHeight);

            const brushg = D3select(this);

            brushg.selectAll('path.timeline')
                .data([data])
                .join('path')
                .attr('class', 'timeline')
                .attr('fill', color)
                .attr('d', area)
                .style('pointer-events', 'none');

            // brush needs its own 'g' to be added last so it's layered over the timeline
            brushg.selectAll('g.brush')
                .data([data])
                .join('g')
                .attr('class', 'brush')
                .call(brush);

            // render the axis (includes the grid)
            const xaxis = D3axisBottom(x)
                .tickSize(height - 2 * handleHang - axisHeight)
                .tickSizeOuter(0)
                .tickFormat(() => null);

            const range = x.domain()[1] - x.domain()[0];
            let minorFn;
            if (range > year) {
                minorFn = d => d.getMonth();
            } else if (range > 7 * day) {
                // xaxis.ticks(D3timeWeek, 1);
                minorFn = d => d.getDate();
            } else {
                xaxis.ticks(D3timeHour, range > 90 * day ? 24 : range > 60 * day ? 12 : 6);
                minorFn = d => d.getHours();
            }

            brushg.selectAll('g.axis.axis--grid')
                .data(['once'])
                .join('g')
                .attr('class', 'axis axis--grid')
                .style('pointer-events', 'none')
                .attr('transform', 'translate(0,' + (handleHang) + ')')
                .call(xaxis)
                .selectAll('.tick')
                .classed('tick--minor', minorFn);

            // date labels
            brushg.selectAll('g.timeline-axis')
                .data(['once'])
                .join('g')
                .attr('class', 'timeline-axis')
                .attr('transform', 'translate(0,' + (height - axisHeight) + ')')
                .attr('text-anchor', 'middle')
                .call(D3axisBottom(x)
                    .tickPadding(0)
                    .tickSizeOuter(0))
                .selectAll('path').remove();

            function brushMoved (e) {
                // exit if it's being reset by this code.
                if (!e.sourceEvent && !brush.moving) return;
                if (!e.selection) {
                    // reset the brush on empty selections
                    setTimeSelection(true);
                    return;
                }
                if (e.selection.length < 2 ||
                    isNaN(e.selection[0]) ||
                    isNaN(e.selection[1])) return; // Ignore invalid selections.
                brush.moving = false;

                // called by user moving slider not by system updating the timeline
                if (!brush.updating) {
                    // outgoing time selection
                    dispatch.call('changed', my, e.selection.map(x.invert));
                }

                brush.resizing = false;
                brush.updating = false;
            }

            function setTimeSelection (rz, lazy) {
                if (!x) return;
                // if (lazy) return
                brush.moving = true;
                brush.resizing = rz;
                brush.updating = true;
                const extent = [sel[0] || x.domain()[0],
                    sel[1] || x.domain()[1]].map(x);
                if (extent[1] - extent[0] >= 0) {
                    brushg.select('.brush').call(brush.move, extent);
                    brushg.selectAll('.handle').attr('display', '');
                }
            }

            setTimeSelection(undefined, false);
        });
    }

    my.width = function (v) {
        if (!arguments.length) return width;
        width = v;
        return my;
    };

    my.height = function (v) {
        if (!arguments.length) return height;
        height = v;
        return my;
    };

    my.dataservice = function (v) {
        if (!arguments.length) return dataservice;
        dataservice = v;
        return my;
    };

    my.color = function (v) {
        if (!arguments.length) return color;
        color = v;
        return my;
    };

    my.start = function (v) {
        if (!arguments.length) return start;
        start = v;
        return my;
    };

    my.end = function (v) {
        if (!arguments.length) return end;
        end = v;
        return my;
    };

    // incoming time selection
    my.sel = function (v) {
        if (!arguments.length) return sel;
        sel = v;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3TimelineBrush;
